import styled from "styled-components";

// Constants
const HEADER_HEIGHT = "52px";
const UX4_HEADER_HEIGHT = "64px";
const CART_V2_HEADER_HEIGHT = "52px";
const CART_V2_HEADER_HEIGHT_WITH_ADDRESS = "64px";
const CART_V2_HEADER_HEIGHT_WITH_EXTENSION = "92px";
const CART_V2_HEADER_HEIGHT_WITH_EXTENSION_WITH_ADDRESS = "106px";
const POP_CART_HEADER_HEIGHT = "52px";
const POP_CART_HEADER_HEIGHT_WITH_EXTENSION = "90px";
const DINEOUT_HEADER_HEIGHT = "64px";
const GIFTING_CART_HEADER_HEIGHT = "90px";
const JIO_HOTSTAR_HEADER_HEIGHT = "38px";

const BLCK_BG_IMAGE_URL = "https://media-assets.swiggy.com/portal/testing/one-black-test/black_plan_gradient.png";
const BLCK_BG_Y_POSITION = "-35px";

// Design constants (declare here when a token is unavailable in theme)
const BLACK_LOTTIE_BACKGROUND = "#280620";
const NINETY_NINE_STORE_GRADIENT = "linear-gradient(94deg, #CFEAFF 32.17%, #F3FAFF 76.95%, #FFF 103.6%)";
const EO_ONE_CART_GRADIENT = "linear-gradient(267deg, #F27823 0.68%, #F3502F 33.37%, #F35E32 65.58%, #F5791C 97.88%)";
const BOLT_BACKGROUND_GRADIENT =
    "radial-gradient(77.81% 228.76% at 96.53% -5.56%, #DD7F56 4.93%, #D15B33 27.82%, #B33B24 42.89%, #7C2037 62.84%, #601334 100%)";
const SHADOW_99_STORE = "0px 4px 8px 0px rgba(2, 6, 12, 0.08)";
const SHADOW_EO_ONE_CART = "0 -4px 28px 0 rgba(0, 0, 0, 0.13)";
const SHADOW_BOLT_OR_THEME = "0px 0px 8px 0px rgba(2, 6, 12, 0.08)";

// Container styles
interface ContainerProps {
    ux4?: boolean;
    isJioHotstarFlow?: boolean;
    isCartError?: boolean;
    "data-jiohotstar"?: string;
}

export const Container = styled.div<ContainerProps>(({ theme, ux4, isJioHotstarFlow, isCartError }) => {
    const baseStyles = {
        width: "100%",
        background: ux4
            ? theme.colors[theme.colorMode].background_Secondary
            : theme.colors[theme.colorMode].background_Primary,
    };

    if (isJioHotstarFlow) {
        return {
            ...baseStyles,
            display: "flex",
            flexDirection: "column",
            height: "100%",
            minHeight: "100%",
            overflow: "hidden",
            background: isCartError ? theme.colors[theme.colorMode].background_Primary : baseStyles.background,
            "@supports (height: var(--sab))": {
                height: `calc(100% - var(--sab, 0px))`,
                minHeight: `calc(100% - var(--sab, 0px))`,
            },
        };
    }

    return {
        ...baseStyles,
        display: "flex",
        flexDirection: "column",
        height: "100%",
        "@supports (height: var(--sab))": {
            height: `calc(100% - var(--sab, 0px))`,
        },
    };
});

// Header styles
interface HeaderProps {
    ux4?: boolean;
    isCart?: boolean;
    isPopCart?: boolean;
    isHeaderExtensionExists?: boolean;
    isPageScrolled?: boolean;
    isIncognitoCart?: boolean;
    isAddressSelected?: boolean;
    isUserBlackMember?: boolean;
    isThemeChange?: boolean;
    isBolt?: boolean;
    isOtg?: boolean;
    isExpress?: boolean;
    isDineoutCartHeader?: boolean;
    isBillPaymentCartHeader?: boolean;
    is99StoreLottie?: boolean;
    isEOCartLottie?: boolean;
    isGiftingCart?: boolean;
    headerGradient?: string;
    headerShadow?: string;
    isJioHotstarFlow?: boolean;
}

const getHeaderHeight = (props: HeaderProps) => {
    const {
        isPopCart,
        isHeaderExtensionExists,
        isAddressSelected,
        isDineoutCartHeader,
        isBillPaymentCartHeader,
        isGiftingCart,
        isJioHotstarFlow,
    } = props;

    if (isGiftingCart) return GIFTING_CART_HEADER_HEIGHT;
    if (isDineoutCartHeader || isBillPaymentCartHeader) return DINEOUT_HEADER_HEIGHT;

    if (isPopCart) {
        if (isHeaderExtensionExists) {
            return POP_CART_HEADER_HEIGHT_WITH_EXTENSION;
        }
        return POP_CART_HEADER_HEIGHT;
    }

    if (isHeaderExtensionExists && isAddressSelected) {
        return CART_V2_HEADER_HEIGHT_WITH_EXTENSION_WITH_ADDRESS;
    }
    if (isHeaderExtensionExists) {
        return CART_V2_HEADER_HEIGHT_WITH_EXTENSION;
    }
    if (isAddressSelected) {
        return CART_V2_HEADER_HEIGHT_WITH_ADDRESS;
    }
    return isJioHotstarFlow ? JIO_HOTSTAR_HEADER_HEIGHT : CART_V2_HEADER_HEIGHT;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getHeaderBackground = (props: HeaderProps, theme: any) => {
    const {
        isUserBlackMember,
        isThemeChange,
        isBolt,
        isOtg,
        isExpress,
        is99StoreLottie,
        isEOCartLottie,
        isDineoutCartHeader,
        isBillPaymentCartHeader,
        ux4,
        headerGradient,
    } = props;
    if (isBillPaymentCartHeader) return "transparent";

    if (isDineoutCartHeader) return theme.colors[theme.colorMode].background_Primary;

    if (headerGradient) {
        return headerGradient;
    }

    if (isThemeChange) {
        // Fallback to frontend gradients
        if (is99StoreLottie) {
            return NINETY_NINE_STORE_GRADIENT;
        }
        if (isEOCartLottie) {
            return `${EO_ONE_CART_GRADIENT} !important`;
        }
        if (isBolt) {
            return BOLT_BACKGROUND_GRADIENT;
        }
        if (isUserBlackMember && (isOtg || isExpress)) {
            return BLACK_LOTTIE_BACKGROUND;
        }
    }

    // Static black plan gradient (non-theme state)
    if (isUserBlackMember && !isThemeChange) {
        if (ux4 || props.isCart || props.isPopCart) {
            return theme.colors[theme.colorMode].background_Primary;
        }
        return undefined;
    }

    if (ux4) {
        // SCSS headerUx4 uses background-primary (container/content use background-secondary)
        return theme.colors[theme.colorMode].background_Primary;
    }

    // Default background for cart-related headers
    if (props.isCart || props.isPopCart) {
        return theme.colors[theme.colorMode].background_Primary;
    }

    return undefined;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getHeaderBoxShadow = (props: HeaderProps, theme: any) => {
    const {
        isThemeChange,
        isBolt,
        is99StoreLottie,
        isOtg,
        isExpress,
        isDineoutCartHeader,
        isEOCartLottie,
        headerShadow,
    } = props;

    if (isDineoutCartHeader) return theme.elevation[theme.colorMode].liftedNegative.boxShadow;

    // Use headerShadow from theme_config if provided
    if (headerShadow) {
        return headerShadow;
    }

    if (isThemeChange) {
        if (is99StoreLottie) {
            return SHADOW_99_STORE;
        }
        if (isEOCartLottie) {
            return SHADOW_EO_ONE_CART;
        }
        if (isBolt || isOtg || isExpress) {
            return SHADOW_BOLT_OR_THEME;
        }
    }

    return undefined;
};

const getBackgroundImageProps = (props: HeaderProps) => {
    const { isUserBlackMember, isThemeChange } = props;

    if (isUserBlackMember && !isThemeChange) {
        return {
            backgroundImage: `url(${BLCK_BG_IMAGE_URL}) !important`,
            backgroundSize: "cover !important",
            backgroundRepeat: "no-repeat !important",
            backgroundPositionY: `${BLCK_BG_Y_POSITION} !important`,
            backgroundPositionX: "right !important",
        } as unknown as Record<string, string>;
    }

    return {};
};

export const Header = styled.div<HeaderProps>(({ theme, ...props }) => {
    const height = getHeaderHeight(props);
    const background = getHeaderBackground(props, theme);
    const boxShadow = getHeaderBoxShadow(props, theme);
    const backgroundImageProps = getBackgroundImageProps(props);

    const shouldApplySafeAreaPadding = (() => {
        // Dineout header never applies safe area padding
        if (props.isBillPaymentCartHeader) return false;

        // Pop Cart
        if (props.isPopCart) {
            if (props.isIncognitoCart) {
                // Serviceable incognito (with address): no padding
                // Unserviceable incognito (no address): padding
                return !props.isAddressSelected;
            }
            return true;
        }

        // Cart v2
        if (props.isIncognitoCart) {
            // Incognito + Address: no padding
            if (props.isAddressSelected) return false;
            // Incognito + Extension + Address: no padding
            if (props.isHeaderExtensionExists && props.isAddressSelected) return false;
            // Incognito (default) and Incognito + Extension (no address): padding
            return true;
        }

        // Base and UX4 headers apply padding
        return true;
    })();

    return {
        display: "flex",
        overflow: "hidden",
        width: "100%",
        alignItems: "flex-start",
        flexWrap: "wrap",
        height,
        minHeight: height,
        position: props.isGiftingCart ? "relative" : "static",
        background,
        boxShadow,
        ...backgroundImageProps,
        paddingTop: shouldApplySafeAreaPadding ? "var(--sat, 0px)" : "0px",

        "@supports (padding-top: var(--sat))": {
            paddingTop: shouldApplySafeAreaPadding ? "var(--sat, 0px)" : "0px",
            height: shouldApplySafeAreaPadding ? `calc(var(--sat, 0px) + ${height})` : height,
            minHeight: shouldApplySafeAreaPadding ? `calc(var(--sat, 0px) + ${height})` : height,
        },
    };
});

// Content styles
interface ContentProps {
    ux4?: boolean;
    isDineoutCart?: boolean;
    isJioHotstarFlow?: boolean;
    hideScrollbar?: boolean;
}

export const Content = styled.div<ContentProps>(({ theme, ux4, isDineoutCart, isJioHotstarFlow, hideScrollbar }) => {
    const headerHeight = ux4 ? UX4_HEADER_HEIGHT : HEADER_HEIGHT;

    const getBackgroundColor = () => {
        if (isDineoutCart) return theme.colors[theme.colorMode].canvas_Primary;
        return ux4 ? theme.colors[theme.colorMode].background_Secondary : "#f4f4f5";
    };

    return {
        flex: 1,
        overflowX: "hidden",
        overflowY: "scroll",
        backgroundColor: getBackgroundColor(),

        ...(hideScrollbar && {
            "&::-webkit-scrollbar": {
                display: "none", // Hide scrollbar for Chrome, Safari and Opera
            },
        }),
        ...(!isJioHotstarFlow && {
            overscrollBehavior: "none",
            height: `calc(100% - ${headerHeight})`,

            "@supports (padding-top: var(--sat))": {
                height: `calc(100% - var(--sat, 0px) - ${headerHeight})`,
            },
        }),
    };
});
